import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { BoardItemActionsPopover } from '@flowwai-frontend/bps-officer-web/boards'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import { ActionConfigInterface, ActionInterface } from '../../types'
import { getTriggerStyles } from './styles'

interface Props {
   isHovered: boolean
   actionsConfig?: ActionConfigInterface | null
   isOpen: boolean
   setIsOpen: (isOpen: boolean) => void

   isSelected?: boolean
}
const NavItemActions = (args: Props): React.ReactElement | null => {
   const { actionsConfig, isHovered, isOpen, setIsOpen } = args

   const { theme } = useAppTheme()
   const { navActions: navTheme } = theme.navbar

   const getPrimaryActions = (actions: ActionInterface[]): ActionInterface[] =>
      actions.filter(action => action.isPrimaryAction)

   const getSecondaryActions = (actions: ActionInterface[]) =>
      actions.filter(action => !action.isPrimaryAction)

   const renderSecondaryActions = (
      actions: ActionInterface[],
      onAction: (id: string) => void
   ): React.ReactElement | null => {
      const shouldShowMoreActions = isHovered || isOpen

      return shouldShowMoreActions ? (
         <div className='mr-[4px]'>
            <BoardItemActionsPopover
               actions={actions ?? []}
               onAction={onAction}
               isOpen={isOpen}
               setIsOpen={setIsOpen}
            />
         </div>
      ) : null
   }

   const renderPrimaryActionButton = (
      action: ActionInterface,
      onAction: (id: string) => void,
      isLast: boolean
   ): React.ReactElement => {
      const onClickAction = (): void => {
         onAction(action.id)
      }

      return (
         <div
            className={getTriggerStyles(navTheme)}
            role={'button'}
            data-testid={`officer-nav-action-${action.id}`}
            onClick={onClickAction}
            title={action.name}
         >
            <svg width={16} height={16} fill='none'>
               <use
                  xlinkHref={`/assets/icons/iconsSprite.svg#${action.iconId}`}
               />
            </svg>
         </div>
      )
   }

   const renderPrimaryActions = (
      actions: ActionInterface[],
      onAction: (id: string) => void
   ): React.ReactElement | null =>
      isHovered ? (
         <>
            {actions.map((action, index) =>
               renderPrimaryActionButton(
                  action,
                  onAction,
                  actions.length - 1 === index
               )
            )}
         </>
      ) : null

   const onClickActionsContainer = (event: React.MouseEvent): void => {
      event.stopPropagation()
      event.preventDefault()
   }

   const renderActions = (): React.ReactElement | null => {
      if (!(actionsConfig && actionsConfig.actions)) return null

      const primaryActions = getPrimaryActions(actionsConfig.actions)

      const secondaryActions = getSecondaryActions(actionsConfig.actions)

      return (
         <div
            className={cn(
               'flex items-center ml-[4px] absolute right-[8px] h-full',
               navTheme.bgColor,
               args.isSelected ? navTheme.selectedBgColor : null
            )}
            onClick={onClickActionsContainer}
         >
            {secondaryActions.length
               ? renderSecondaryActions(
                    secondaryActions,
                    actionsConfig.onAction
                 )
               : null}

            {primaryActions.length
               ? renderPrimaryActions(primaryActions, actionsConfig.onAction)
               : null}
         </div>
      )
   }

   return renderActions()
}

export default observer(NavItemActions)
